import React, { useEffect, FunctionComponent, useState, MouseEvent } from 'react';
import { connect } from 'react-redux';
import { equals, toPairs, filter as filterR } from 'ramda';
import { FormattedMessage } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { ContactType } from '../../../../common/enums/ContactType';
import { IReferenceScore } from '../../../../common/models/reference/IReferenceScore';
import { IBindingCallback1 } from '../../../../common/models/callback';
import { IAppState } from '../../../../common/models/store/IAppState';
import {
  fetchReferencesRoutine,
  fetchCandidateDataRoutine
} from '../../routines';
import Table from '../../../../components/Table';
import { getColumns } from './utils';
import { IFilter } from '../../../../common/models/filter/IFilter';
import { IContactFilter } from '../../../../common/models/filter/IContactFilter';
import { IProfileCandidate } from '../../../../common/models/candidate/IProfileCandidate';
import { ISelectedRole } from '../../../../common/models/contact/ISelectedRole';
import { IContact } from '../../../../common/models/contact/IContact';
import { IRowAccessor } from '../../../../common/models/table/IRowAccessor';
import { showSurveyBuilderModalRoutine, showContactProfileModalRoutine } from '../../../../routines/history';
import CandidateDropdown from '../CandidateDropdown';
import UserLogo from '../../../../components/UserLogo';
import Icon from '../../../../components/Icon';
import RegularButton from '../../../../components/RegularButton';
import LoaderWrapper from '../../../../components/LoaderWrapper';
import { CandidateStatus } from '../../../../common/enums/CandidateStatus';
import CustomBadge from '../../../../components/CustomBadge';
import { inactiveProgressMessage } from '../../../../common/helpers/dashboardHelper';
import AvgScore from '../../../../components/AvgScore';
import { getAttributeColors } from '../../../../common/helpers/colorHelper';
import ProvidedReferencesPopup from '../../components/ProvidedReferencesPopup';
import { ProfileRefCounters } from '../../../../common/models/reference/ProfileRefCounters';
import CandidateActionsPopup from '../CandidateActionsPopup';
import { setReportCandidatesRoutine } from '../../../../containers/ShareReportModal/routines';
import { SurveyStatus } from '../../../../common/enums/SurveyStatus';
import LinkButton from '../../../../components/LinkButton';
import Illustration from '../../../../components/Illustration';
import { CandidateActionType } from '../CandidateActionsPopup/utils';
import ReviewReferencesModal from '../../components/ReviewReferencesModal';
import styles from './styles.module.scss';
import { Routes } from '../../../../common/enums/Routes';
import EmptyFieldPlaceholder from '../../../../components/EmptyFieldPlaceholder';

interface IProps extends RouteComponentProps {
  data: IProfileCandidate | undefined;
  contact: IContact;
  loading: boolean;
  references: IReferenceScore[];
  candidateId: string;
  loadingCandidateData: boolean;
  fetchReferences: IBindingCallback1<IContactFilter>;
  fetchCandidateData: IBindingCallback1<string>;
  showContactProfileModal: IBindingCallback1<ISelectedRole>;
  showSurveyBuilderModal: IBindingCallback1<string>;
  setReportCandidates: IBindingCallback1<string[]>;
}

const CandidatePane: FunctionComponent<IProps> = ({
  data,
  contact,
  loading,
  history,
  references,
  candidateId,
  loadingCandidateData,
  fetchReferences,
  fetchCandidateData,
  setReportCandidates,
  showSurveyBuilderModal,
  showContactProfileModal
}) => {
  const defaultFilter: IContactFilter = {
    id: candidateId,
    sort: [{ id: 'status', desc: true }],
    filter: []
  };
  const [filter, setFilter] = useState(defaultFilter);
  const [shownAction, setShownAction] = useState<CandidateActionType | null>(null);
  const [changeStatusIds, setChangeStatusIds] = useState<string[]>([]);

  useEffect(() => {
    if (contact) {
      fetchCandidateData(candidateId);
      setFilter(defaultFilter);
    }
  }, [candidateId]);

  useEffect(() => {
    if (contact) {
      fetchReferences(filter);
    }
  }, [filter]);

  const updateFilter = (newFilter: Partial<IFilter>) => {
    const updatedFilter = { ...filter, ...newFilter };

    if (!equals(updatedFilter, filter)) {
      setFilter(updatedFilter);
    }
  };

  const onCellClick = ({ accessor, original }: IRowAccessor<IReferenceScore>, event: MouseEvent<HTMLElement>) => {
    switch (accessor) {
      case 'name':
        showContactProfileModal({ contactId: original.contactId, role: ContactType.Reference });
        break;
      case 'survey':
        event.stopPropagation();
        break;
      default:
        break;
    }
  };

  const isActive = data?.status === CandidateStatus.Active;

  return (
    <>
      <div className="profile-pane-main">
        <LoaderWrapper transparent loading={loadingCandidateData || loading}>
          <ReviewReferencesModal
            ids={changeStatusIds}
            onHide={() => setChangeStatusIds([])}
          />
          <div className={styles.candidateProfileHeader}>
            <div className="d-flex align-items-center">
              <span className={styles.dropdownLabel}>
                <FormattedMessage id="position" defaultMessage="Position" />
              </span>
              <CandidateDropdown />
            </div>
            {!isActive && (
              <div className="ml-4">
                <CustomBadge status={data?.status}>
                  <FormattedMessage id="inactive" defaultMessage="Inactive" />
                  &nbsp;-&nbsp;
                  {inactiveProgressMessage(data?.status as CandidateStatus)}
                </CustomBadge>
              </div>
            )}
            {data && (
              <>
                <div className={styles.initialsContainer}>
                  <span className={styles.initialsLabel}>
                    <FormattedMessage id="candidateTable.assignedUser" defaultMessage="Assigned user" />
                  </span>
                  {data.owner
                    ? <UserLogo user={data.owner} />
                    : <EmptyFieldPlaceholder />}
                </div>
                <div className={styles.headerBttns}>
                  <CandidateActionsPopup shownAction={shownAction} setShownAction={setShownAction} />
                  <RegularButton
                    icon={<Icon icon="chart" />}
                    textIntlId="runReport"
                    defaultMessage="Run report"
                    disabled={!filterR(ref => ref.status === SurveyStatus.Finished, references).length}
                    variant={data.status === CandidateStatus.Active ? 'primary' : 'tertiary'}
                    onClick={() => setReportCandidates([candidateId])}
                  />
                </div>
              </>
            )}
          </div>
          {references.length
            ? (
              <Table
                config={{
                  columns: getColumns(
                    {
                      onSurveyClick: showSurveyBuilderModal,
                      onUpdateStatus: id => setChangeStatusIds([id]),
                      onManualInput: id => history.push(`${Routes.ManualInput}/${id}`)
                    },
                    isActive
                  ),
                  onFilterUpdated: updateFilter,
                  data: { items: references, total: references.length },
                  loading: loadingCandidateData || loading,
                  sortingEnabled: true,
                  filteringEnabled: true,
                  onCellClicked: onCellClick,
                  defaultSorted: filter.sort
                }}
              />
            )
            : (
              <Illustration
                variant="fisher"
                height="20.5rem"
                header={(
                  <FormattedMessage
                    id="contactProfile.candidate.waitingForReferences"
                    defaultMessage="Waiting for candidate..."
                  />
                )}
                description={(
                  <FormattedMessage
                    id="contactProfile.candidate.waitingForReferencesHint"
                    defaultMessage={`This candidate hasn’t provided their references yet.
                    Once they do, references’ information will be shown here.`}
                  />
                )}
                button={(
                  <LinkButton
                    textIntlId="contactProfile.candidate.sendReminder"
                    defaultMessage="Send reminder to candidate"
                    icon={<Icon icon="reminder" />}
                    iconPosition="right"
                    onClick={() => setShownAction('reminder')}
                  />
                )}
              />
            )}
        </LoaderWrapper>
      </div>
      <footer className="profile-pane-footer d-flex justify-content-between align-items-center">
        <ProvidedReferencesPopup
          counters={data?.referenceCounters || {} as ProfileRefCounters}
          references={references}
        />
        <div className="d-flex">
          {data && toPairs(data.counters).map(([key, val]) => (val
            ? <AvgScore intlId={key} defaultMessage={key} key={key} score={val} color={getAttributeColors(key).color} />
            : null
          ))}
          <AvgScore score={data?.score as number} intlId="totalScore" defaultMessage="Total score" />
        </div>
      </footer>
    </>
  );
};

const mapStateToProps = ({
  contactProfile: {
    references: {
      items,
      loading,
      candidateData,
      loadingCandidateData
    },
    contact: { contact, candidateId }
  }
}: IAppState) => ({
  references: items,
  loading,
  data: candidateData,
  loadingCandidateData,
  contact,
  candidateId
});

const mapDispachToProps = {
  fetchReferences: fetchReferencesRoutine,
  fetchCandidateData: fetchCandidateDataRoutine,
  showSurveyBuilderModal: showSurveyBuilderModalRoutine,
  showContactProfileModal: showContactProfileModalRoutine,
  setReportCandidates: setReportCandidatesRoutine
};

export default connect(mapStateToProps, mapDispachToProps)(withRouter(CandidatePane));
